import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { needsAPI } from '../lib/api';
import { 
  ArrowLeft, Loader2, MapPin, Calendar, Euro, Clock, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';

const positionColors = {
  GK: 'bg-yellow-500 text-yellow-950',
  DEF: 'bg-blue-500 text-white',
  MID: 'bg-green-500 text-white',
  FWD: 'bg-red-500 text-white',
};

const positionLabels = {
  GK: 'Portera',
  DEF: 'Defensa',
  MID: 'Mediocampista',
  FWD: 'Delantera',
};

const priorityLabels = {
  high: 'Alta',
  medium: 'Media',
  low: 'Baja',
};

const priorityColors = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200',
};

export const NeedDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [need, setNeed] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNeed();
  }, [id]);

  const loadNeed = async () => {
    try {
      const response = await needsAPI.getOne(id);
      setNeed(response.data);
    } catch (error) {
      console.error('Error loading need:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!need) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Necesidad no encontrada</p>
          <Button onClick={() => navigate('/needs')} className="mt-4">
            Volver al listado
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const clubLogo = `https://api.dicebear.com/7.x/identicon/svg?seed=${need.club_name}`;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="need-detail-page">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="border-l-4 border-l-indigo-500 p-6 md:p-8 bg-slate-50">
            <div className="flex items-start gap-6">
              <img
                src={clubLogo}
                alt={need.club_name}
                className="w-20 h-20 rounded-xl bg-white p-2 border border-slate-200"
              />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-bold border ${priorityColors[need.priority]}`}>
                    Prioridad {priorityLabels[need.priority]}
                  </span>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800 uppercase tracking-tight">
                  {need.club_name}
                </h1>
                <p className="text-slate-500 mt-1">
                  {need.club_league} • {need.club_country}
                </p>
              </div>
            </div>
          </div>

          {/* Position wanted */}
          <div className="p-6 md:p-8 border-b border-slate-200">
            <div className="flex items-center gap-4">
              <span className={`w-16 h-16 rounded-xl flex items-center justify-center font-heading font-bold text-xl ${positionColors[need.position]}`}>
                {need.position}
              </span>
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wider">Busca</p>
                <p className="font-heading text-2xl font-bold text-slate-800 uppercase">
                  {positionLabels[need.position]}
                </p>
              </div>
              <div className="ml-auto">
                <span className={`text-sm uppercase tracking-wider font-bold ${
                  need.type === 'transfer' ? 'text-lime-600' :
                  need.type === 'loan' ? 'text-amber-600' : 'text-indigo-600'
                }`}>
                  {need.type === 'transfer' ? 'Transferencia' :
                   need.type === 'loan' ? 'Préstamo' : 'Transferencia o Préstamo'}
                </span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            {/* Quick info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {(need.min_age || need.max_age) && (
                <div className="stat-card">
                  <Calendar className="w-5 h-5 text-indigo-500 mb-2" />
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Rango de edad</p>
                  <p className="font-heading text-xl font-bold text-slate-800">
                    {need.min_age && need.max_age
                      ? `${need.min_age}-${need.max_age} años`
                      : need.min_age
                      ? `+${need.min_age} años`
                      : `-${need.max_age} años`}
                  </p>
                </div>
              )}
              {need.budget && (
                <div className="stat-card">
                  <Euro className="w-5 h-5 text-indigo-500 mb-2" />
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Presupuesto</p>
                  <p className="font-heading text-xl font-bold text-slate-800">
                    {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(need.budget)}
                  </p>
                </div>
              )}
              {need.max_salary && (
                <div className="stat-card">
                  <Euro className="w-5 h-5 text-lime-500 mb-2" />
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Salario máximo</p>
                  <p className="font-heading text-xl font-bold text-slate-800">
                    {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(need.max_salary)}/año
                  </p>
                </div>
              )}
              {need.deadline && (
                <div className="stat-card">
                  <Clock className="w-5 h-5 text-red-500 mb-2" />
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Fecha límite</p>
                  <p className="font-heading text-lg font-bold text-slate-800">{need.deadline}</p>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h2 className="font-heading text-lg font-bold text-slate-800 uppercase tracking-tight mb-4 border-b border-slate-200 pb-2">
                  Requisitos
                </h2>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Posición</dt>
                    <dd className="font-medium text-slate-800">{positionLabels[need.position]}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Tipo de operación</dt>
                    <dd className="font-medium text-slate-800">
                      {need.type === 'transfer' ? 'Transferencia' :
                       need.type === 'loan' ? 'Préstamo' : 'Transferencia o Préstamo'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Prioridad</dt>
                    <dd className="font-medium text-slate-800">{priorityLabels[need.priority]}</dd>
                  </div>
                  {need.preferred_nationalities && need.preferred_nationalities.length > 0 && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Nacionalidades preferidas</dt>
                      <dd className="font-medium text-slate-800 text-right">
                        {need.preferred_nationalities.join(', ')}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              <div>
                <h2 className="font-heading text-lg font-bold text-slate-800 uppercase tracking-tight mb-4 border-b border-slate-200 pb-2">
                  ¿Tienes una jugadora para este club?
                </h2>
                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                  <p className="font-medium text-indigo-800">Contacta con Solo Cracks</p>
                  <p className="text-sm text-indigo-600 mt-1">
                    Si tienes una jugadora que encaja con estos requisitos, avísanos y gestionamos la operación.
                  </p>
                </div>
              </div>
            </div>

            {need.description && (
              <div className="mt-8">
                <h2 className="font-heading text-lg font-bold text-slate-800 uppercase tracking-tight mb-4 border-b border-slate-200 pb-2">
                  Descripción
                </h2>
                <p className="text-slate-600 whitespace-pre-wrap">{need.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NeedDetailPage;
